import React, { useMemo } from 'react';
import { AlertTriangle, TrendingDown, TrendingUp, Info, Sparkles } from 'lucide-react';

/**
 * ResultEstimateIndicator - Prominent display of available search results
 * 
 * Provides proactive guidance to users about their search refinement:
 * - Color-coded count (green → yellow → orange → red)
 * - Warning messages when count is low
 * - Suggestions for expanding search
 * - Animated transitions for count changes
 * 
 * DEX Standard: Premium minimalism, proactive guidance
 * KAI Standard: Glass Box transparency, user always knows state
 * 
 * @param {number} estimatedResults - Current estimated result count
 * @param {number} previousResults - Previous result count (for trend)
 * @param {boolean} loading - Show loading state
 * @param {string} size - 'compact' | 'default' | 'prominent'
 * @param {function} onSuggestionClick - Handler for suggestion clicks
 * @param {Array} suggestions - Array of suggestion objects {label, action}
 * @param {boolean} showTrend - Whether to show trend indicator
 */
export default function ResultEstimateIndicator({
  estimatedResults = null,
  previousResults = null,
  loading = false,
  size = 'default',
  onSuggestionClick,
  suggestions = [],
  showTrend = true,
}) {
  // Determine the status level based on result count
  const status = useMemo(() => {
    if (estimatedResults === null || loading) return 'neutral';
    if (estimatedResults === 0) return 'critical';
    if (estimatedResults < 5) return 'warning';
    if (estimatedResults < 20) return 'caution';
    return 'healthy';
  }, [estimatedResults, loading]);
  
  // Calculate trend
  const trend = useMemo(() => {
    if (previousResults === null || estimatedResults === null) return null;
    if (estimatedResults > previousResults) return 'up';
    if (estimatedResults < previousResults) return 'down';
    return 'stable';
  }, [estimatedResults, previousResults]);
  
  // Status-based styling
  const statusStyles = {
    healthy: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      countText: 'text-emerald-800',
      icon: null,
    },
    caution: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      countText: 'text-amber-800',
      icon: <Info className="w-3.5 h-3.5" />,
    },
    warning: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-700',
      countText: 'text-orange-800',
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
    },
    critical: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      countText: 'text-red-800',
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
    },
    neutral: {
      bg: 'bg-black/5',
      border: 'border-black/10',
      text: 'text-black/60',
      countText: 'text-black/80',
      icon: null,
    },
  };
  
  const style = statusStyles[status];
  
  // Size variants
  const sizeStyles = {
    compact: {
      container: 'px-2 py-1',
      text: 'text-xs',
      count: 'text-sm font-semibold',
    },
    default: {
      container: 'px-3 py-2',
      text: 'text-xs',
      count: 'text-base font-bold',
    },
    prominent: {
      container: 'px-4 py-3',
      text: 'text-sm',
      count: 'text-xl font-bold',
    },
  };
  
  const sizeStyle = sizeStyles[size];
  
  // Get contextual message
  const getMessage = () => {
    if (loading) return 'Calculating...';
    if (estimatedResults === 0) return 'No matches found';
    if (estimatedResults === null) return 'Enter criteria to see matches';
    if (estimatedResults < 5) return 'Very limited results';
    if (estimatedResults < 20) return 'Limited results';
    return 'Available matches';
  };
  
  // Get suggestion message for low results
  const getSuggestionMessage = () => {
    if (status === 'critical') {
      return 'Try removing filters or broadening your search criteria';
    }
    if (status === 'warning') {
      return 'Consider expanding location or adjusting job titles';
    }
    return null;
  };
  
  // Don't render if no data and not loading
  if (estimatedResults === null && !loading) {
    return null;
  }
  
  return (
    <div
      className={`
        rounded-xl border transition-all duration-300 ease-out
        ${style.bg} ${style.border} ${sizeStyle.container}
        ${status === 'critical' ? 'animate-pulse-subtle' : ''}
      `}
      style={{
        animation: status === 'critical' ? 'eq-attention-pulse 2s ease-in-out infinite' : undefined,
      }}
    >
      <style>{`
        @keyframes eq-attention-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
          50% { box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1); }
        }
        @keyframes eq-count-change {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      `}</style>
      
      <div className="flex items-center justify-between gap-3">
        {/* Left: Count and label */}
        <div className="flex items-center gap-2">
          {style.icon && (
            <div className={style.text}>
              {style.icon}
            </div>
          )}
          
          {!style.icon && status === 'healthy' && (
            <div className="text-emerald-500">
              <Sparkles className="w-3.5 h-3.5" />
            </div>
          )}
          
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span 
                className={`${sizeStyle.count} ${style.countText} tabular-nums`}
                key={estimatedResults} // Force re-render animation on change
                style={{
                  animation: previousResults !== estimatedResults ? 'eq-count-change 0.3s ease-out' : undefined,
                }}
              >
                {loading ? '...' : estimatedResults === 0 ? '0' : `~${estimatedResults.toLocaleString()}`}
              </span>
              <span className={`${sizeStyle.text} ${style.text}`}>
                {getMessage()}
              </span>
            </div>
            
            {/* Suggestion message for low results */}
            {getSuggestionMessage() && (
              <p className={`${sizeStyle.text} ${style.text} opacity-80 mt-0.5`}>
                {getSuggestionMessage()}
              </p>
            )}
          </div>
        </div>
        
        {/* Right: Trend indicator */}
        {showTrend && trend && !loading && (
          <div className={`
            flex items-center gap-1 px-2 py-0.5 rounded-full text-xs
            ${trend === 'up' ? 'bg-emerald-100 text-emerald-700' : ''}
            ${trend === 'down' ? 'bg-red-100 text-red-700' : ''}
            ${trend === 'stable' ? 'bg-gray-100 text-gray-600' : ''}
          `}>
            {trend === 'up' && <TrendingUp className="w-3 h-3" />}
            {trend === 'down' && <TrendingDown className="w-3 h-3" />}
            {trend === 'up' && <span>+{estimatedResults - previousResults}</span>}
            {trend === 'down' && <span>-{previousResults - estimatedResults}</span>}
          </div>
        )}
      </div>
      
      {/* Suggestions for expanding search */}
      {(status === 'critical' || status === 'warning') && suggestions.length > 0 && (
        <div className="mt-2 pt-2 border-t border-current/10">
          <p className={`${sizeStyle.text} ${style.text} font-medium mb-1.5`}>
            Suggestions:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => onSuggestionClick?.(suggestion)}
                className={`
                  px-2.5 py-1 rounded-lg text-xs font-medium
                  bg-white border border-current/20
                  ${style.text}
                  hover:bg-white/80 hover:scale-105 active:scale-95
                  transition-all duration-200
                `}
              >
                {suggestion.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Compact inline version for headers
 */
export function ResultEstimateBadge({ 
  estimatedResults, 
  loading = false,
  className = '' 
}) {
  if (estimatedResults === null && !loading) return null;
  
  const status = loading ? 'neutral' :
    estimatedResults === 0 ? 'critical' :
    estimatedResults < 5 ? 'warning' :
    estimatedResults < 20 ? 'caution' : 'healthy';
  
  const styles = {
    healthy: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    caution: 'bg-amber-100 text-amber-700 border-amber-200',
    warning: 'bg-orange-100 text-orange-700 border-orange-200',
    critical: 'bg-red-100 text-red-700 border-red-200',
    neutral: 'bg-gray-100 text-gray-600 border-gray-200',
  };
  
  return (
    <span className={`
      inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
      border ${styles[status]} ${className}
    `}>
      {loading ? (
        <span className="animate-pulse">...</span>
      ) : (
        <>
          <span className="tabular-nums">~{estimatedResults.toLocaleString()}</span>
          <span className="opacity-70">available</span>
        </>
      )}
    </span>
  );
}
